import React, { useState, useEffect } from 'react'
import { Modal, Button, Table, Spinner, Alert } from 'react-bootstrap'
import { expanceListingAPI } from '../../../API/litigationApis' // Adjust the import path as needed
import { showToast } from "../../../utils/handleToast"

const ViewExpensesModal = ({ showExpensesModal, setShowExpensesModal, selectedCase }) => {
    const [expenses, setExpenses] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Fetch expenses when modal opens
    useEffect(() => {
        if (showExpensesModal && selectedCase?.case_id) {
            fetchExpenses()
        }
    }, [showExpensesModal, selectedCase])

    const fetchExpenses = async () => {
        try {
            setLoading(true)
            setError(null)
            
            const params = {
                case_id: selectedCase.case_id
            }
            
            const response = await expanceListingAPI(params)
            
            if (response?.status && response?.data) {
                setExpenses(response.data.results || [])
            } else {
                throw new Error(response?.message || "Failed to fetch expenses")
            }
        } catch (error) {
            console.error("Error fetching expenses:", error)
            setError(error.message || "Failed to fetch expenses")
            showToast({
                variant: "error",
                message: error.message || "Failed to fetch expenses"
            })
        } finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setShowExpensesModal(false)
        setExpenses([])
        setError(null)
    }

    // Format currency helper
    const formatCurrency = (amount) => {
        if (!amount) return '₹0'
        return `₹${parseFloat(amount).toLocaleString('en-IN')}`
    }

    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return '--'
        try {
            const date = new Date(dateString)
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
        } catch (err) {
            return '--'
        }
    }

    // Calculate total expenses
    const totalExpenses = expenses.reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0)

    return (
        <Modal 
            show={showExpensesModal} 
            onHide={handleClose}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Pocket Expenses
                    {selectedCase && (
                        <div className="text-muted fs-6 mt-1">
                            {selectedCase.customer_name} ({selectedCase.lan_number})
                        </div>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2 text-muted">Loading expenses...</p>
                    </div>
                ) : error ? (
                    <Alert variant="danger">
                        <Alert.Heading>Error</Alert.Heading>
                        <p>{error}</p>
                        <Button variant="outline-danger" size="sm" onClick={fetchExpenses}>
                            Try Again
                        </Button>
                    </Alert>
                ) : expenses.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-muted">No expenses found for this case.</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <div className="row">
                                <div className="col-md-6">
                                    <small className="text-muted">Total Expenses:</small>
                                    <div className="fw-bold fs-5">
                                        {formatCurrency(totalExpenses)}
                                    </div>
                                </div>
                                <div className="col-md-6 text-end">
                                    <small className="text-muted">
                                        {expenses.length} expense(s) recorded
                                    </small>
                                </div>
                            </div>
                        </div>
                        
                        <div className="table-responsive">
                            <Table striped hover>
                                <thead className="table-gray">
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Expense Type</th>
                                        <th>Amount</th>
                                        <th>Date Added</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses.map((expense, index) => (
                                        <tr key={expense.id}>
                                            <td>{index + 1}</td>
                                            <td>
                                                <span className="text-capitalize">
                                                    {expense.expense_type}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold text-success">
                                                    {formatCurrency(expense.amount)}
                                                </span>
                                            </td>
                                            <td>
                                                <small className="text-muted">
                                                    {formatDate(expense.created_at)}
                                                </small>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                {expenses.length > 0 && (
                    <Button variant="outline-primary" onClick={fetchExpenses}>
                        Refresh
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default ViewExpensesModal